import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, fetchJSON } from "../api/client";
import type { Station, Train } from "../api/types";

type CatalogValue = {
  trains: Train[];
  stations: Station[];
  trainsById: Map<number, Train>;
  stationsById: Map<number, Station>;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
};

const CatalogContext = createContext<CatalogValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [trains, setTrains] = useState<Train[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const reload = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchJSON<Train[]>("/v1/trains"), fetchJSON<Station[]>("/v1/stations")])
      .then(([nextTrains, nextStations]) => {
        if (cancelled) return;
        setTrains(Array.isArray(nextTrains) ? nextTrains : []);
        setStations(Array.isArray(nextStations) ? nextStations : []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err), "internal", 0));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return reload();
  }, [reload]);

  const value = useMemo<CatalogValue>(
    () => ({
      trains,
      stations,
      trainsById: new Map(trains.map((t) => [t.train_id, t])),
      stationsById: new Map(stations.map((s) => [s.station_id, s])),
      loading,
      error,
      reload: () => {
        reload();
      },
    }),
    [trains, stations, loading, error, reload],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog(): CatalogValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used within CatalogProvider");
  return ctx;
}
